import * as Sentry from "@sentry/react";
import { getRecaptchaAuthErrorMeta, isRecaptchaDebugAuthError } from "../components/SignIn/recaptchaUtils";

const isSentryEnabled = () =>
  process.env.REACT_APP_ENVIRONMENT === "production" && Boolean(process.env.REACT_APP_SENTRY_DSN);

export const initSentry = () => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    initialScope: {
      tags: {
        hospital: process.env.REACT_APP_HOSPITAL_NAME || "unknown",
      },
    },
  });
};

const withRecaptchaScope = (callback) => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("context", "recaptcha");
    callback(scope);
  });
};

const applyRecaptchaErrorMeta = (scope, error, context = {}) => {
  const meta = getRecaptchaAuthErrorMeta(error);

  scope.setTag("firebaseErrorCode", meta.firebaseErrorCode);
  scope.setTag("recaptchaErrorCategory", meta.errorCategory);
  scope.setContext("recaptcha", {
    ...context,
    ...meta,
  });
};

export const captureRecaptchaException = (error, context = {}) => {
  withRecaptchaScope((scope) => {
    applyRecaptchaErrorMeta(scope, error, context);
    Sentry.captureException(error);
  });
};

export const captureRecaptchaMessage = (message, level = "warning", context = {}) => {
  withRecaptchaScope((scope) => {
    scope.setContext("recaptcha", context);
    Sentry.captureMessage(message, level);
  });
};

/**
 * Debug-only reporting for Firebase phone auth / reCAPTCHA errors.
 * Does not affect business logic — call from catch blocks or hooks for observability.
 * Returns true when the error was reported to Sentry.
 */
export const captureRecaptchaAuthError = (error, context = {}) => {
  if (!isSentryEnabled() || !error) {
    return false;
  }

  const code = error?.code;
  if (!code || !isRecaptchaDebugAuthError(error)) {
    return false;
  }

  withRecaptchaScope((scope) => {
    applyRecaptchaErrorMeta(scope, error, context);
    Sentry.captureException(error);
  });

  return true;
};

/**
 * A Firestore listener that has not delivered its first snapshot in time.
 * Ids only: the context must never carry patient data.
 */
export const captureFirestoreListenerStall = ({ listener, databaseId, uid, timeoutMs }) => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("context", "firestore-listener");
    scope.setTag("firestoreListener", listener);
    scope.setContext("firestoreListener", { listener, databaseId, uid, timeoutMs });
    Sentry.captureMessage("Firestore listener has not delivered its first snapshot", "warning");
  });
};

/**
 * Reports known Firebase auth errors via captureRecaptchaAuthError,
 * falls back to captureRecaptchaException for unclassified errors.
 */
export const reportRecaptchaAuthError = (error, context = {}) => {
  if (!isSentryEnabled() || !error) {
    return;
  }

  if (!captureRecaptchaAuthError(error, context)) {
    captureRecaptchaException(error, { ...context, reportedVia: "fallback" });
  }
};
